import React, { useState } from "react";
import {
  List,
  DataTable,
  NumberField,
  TextField,
  TextInput,
  ReferenceInput,
  BooleanInput,
  Edit,
  ReferenceField,
  BooleanField,
  SimpleForm,
  SelectInput,
  EditButton,
  NumberInput,
  Create,
  Pagination,
  Show,
  FunctionField,
  useRecordContext,
  useListContext,
  Button,
  TopToolbar,
  FilterButton,
  ColumnsButton,
  TabbedShowLayout,
  Tab,
  useNotify,
} from "react-admin";

// Lib
import { custInfo } from "../lib/customer";
import { hasScope, SCOPE } from "../lib/auth";
import { DetailsButton } from "../lib/detailsButton";
import { ChatBubbleTooltip } from "../lib/chatBubbleTooltip";
import { ContextTagField } from "../lib/contextTagField";
import { CustomDatetimeField, CustomDateTimeInput } from "../lib/datetime";
import { WineColourField } from "../lib/wineColour";
import { DownloadCSV } from "../lib/csvDownload";
import { CONFIG } from "../lib/appConfig";
import { AliasOrBarcodeReference } from "../lib/aliasWithBarcodeTooltip";
import RangePickerInput from "../lib/dateRangePicker";
import { LotNameField } from "./lots";

// MUI
import IconDownload from "@mui/icons-material//CloudDownload";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import IconCancel from "@mui/icons-material//Cancel";
import DialogActions from "@mui/material/DialogActions";
import Link from "@mui/material/Link";

// Other
import { CustomTab } from "../lib/customTab";

const fso2Filters = [
  <TextInput source="barcodeOrAlias" label="Vessel" alwaysOn resettable />,
  <TextInput source="lotName" label="Lot" alwaysOn resettable />,
  <NumberInput label="Less Than" source="fso2LTE" style={{ width: "120px" }} />,
  <NumberInput
    label="Greater Than"
    source="fso2GTE"
    style={{ width: "120px" }}
  />,
  <TextInput source="deviceSerial" label="Device" resettable />,
  <TextInput source="intendedUse" label="Intended Use" resettable />,
  <RangePickerInput source="dateRange" label="Date" />,
  <SelectInput
    source="type"
    choices={[
      { id: 1, name: "Barrel" },
      { id: 2, name: "Lot Composite" },
      { id: 3, name: "Ad Hoc" },
    ]}
  />,
];

const TicketField = ({ source }: any) => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <span>
      <Link
        href={
          CONFIG.GrafanaServer +
          "/d/diag/diagnostics?orgId=8&refresh=5s&var-ticket=" +
          record[source]
        }
        target="_new"
      >
        {record[source]}
      </Link>
    </span>
  );
};

const DeviceTitleField = (props) => (
  <FunctionField
    {...props}
    render={(record) =>
      record.deviceSerial === null ? "FS1" : `FS1 ${record.deviceSerial}`
    }
  />
);

const FSO2ExportButton = () => {
  const [showDialog, setShowDialog] = useState(false);
  const { sort, filterValues } = useListContext();
  const notify = useNotify();
  const showValidMeasurementsOnly = !hasScope(SCOPE.DIAGNOSTICS);

  const handleDownload = async () => {
    setShowDialog(false);

    if (showValidMeasurementsOnly) {
      filterValues.valid = true;
    }

    try {
      await DownloadCSV(
        "BarrelWise_FSO2",
        custInfo.customerID,
        "events/fso2_reading",
        sort,
        filterValues,
      );
    } catch (err) {
      console.log(err);
      notify(`Failed to download CSV: ${err}`, { type: "error" });
    }
  };

  return (
    <>
      <Button
        onClick={() => {
          setShowDialog(true);
        }}
        label="Export"
      >
        <IconDownload />
      </Button>
      <Dialog fullWidth open={showDialog}>
        <DialogTitle>CSV Export</DialogTitle>
        <DialogActions>
          <Button
            label="ra.action.cancel"
            variant="outlined"
            onClick={() => {
              setShowDialog(false);
            }}
          >
            <IconCancel />
          </Button>
          <Button label="Download" variant="contained" onClick={handleDownload}>
            <IconDownload />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const FSO2ListActions = () => {
  return (
    <TopToolbar>
      <FilterButton />
      <FSO2ExportButton />
      <ColumnsButton />
    </TopToolbar>
  );
};

export const FSO2List = () => (
  <List
    perPage={100}
    pagination={<Pagination rowsPerPageOptions={[100, 200, 500, 1000]} />}
    filters={fso2Filters}
    actions={<FSO2ListActions />}
    sort={{ field: "datetime", order: "DESC" }}
    storeKey={false}
    empty={false}
  >
    <DataTable bulkActionButtons={false} rowClick={false}>
      <DataTable.Col source="barrelID" label="Vessel">
        <AliasOrBarcodeReference
          source="barrelID"
          reference="vessels"
          label="Vessel"
        />
      </DataTable.Col>
      <DataTable.Col source="lotID" label="Lot">
        <ReferenceField source="lotID" reference="lots" link="show">
          <LotNameField />
        </ReferenceField>
      </DataTable.Col>

      <DataTable.Col source="blendID" label="Blend">
        <ReferenceField source="blendID" reference="blends" link="show">
          <TextField source="name" />
        </ReferenceField>
      </DataTable.Col>
      <DataTable.Col source="intendedUse" label="Intended Use">
        <TextField source="intendedUse" label="Intended Use" />
      </DataTable.Col>
      <DataTable.Col source="fso2" label="Free SO₂ (ppm)">
        <NumberField
          source="fso2"
          label={"Free SO\u2082 (ppm)"}
          options={{ maximumFractionDigits: 0 }}
        />
      </DataTable.Col>
      <DataTable.Col source="datetime" label="Measurement Time">
        <CustomDatetimeField source="datetime" label="Measurement Time" />
      </DataTable.Col>
      {hasScope(SCOPE.DIAGNOSTICS) && (
        <DataTable.Col source="ticket" label="Ticket" disableSort>
          <TicketField source="ticket" label="Ticket" />
        </DataTable.Col>
      )}

      {hasScope(SCOPE.DIAGNOSTICS) && (
        <DataTable.Col source="valid" label="Valid">
          <BooleanField source="valid" label="Valid" />
        </DataTable.Col>
      )}
      <DataTable.Col source="device" label="Device" disableSort>
        <DeviceTitleField label="Device" />
      </DataTable.Col>
      <DataTable.Col source="notes" label="Notes" disableSort>
        <ChatBubbleTooltip source="notes" />
      </DataTable.Col>
      <DataTable.Col>
        <DetailsButton />
      </DataTable.Col>
      {hasScope(SCOPE.DIAGNOSTICS) && (
        <DataTable.Col>
          <EditButton />
        </DataTable.Col>
      )}
    </DataTable>
  </List>
);

// Available to admin only
export const FSO2Edit = () => (
  <Edit redirect="list">
    <SimpleForm>
      <TextInput source="ticket" readOnly />
      <ReferenceInput source="barrelID" reference="vessels">
        <SelectInput optionText="barcode" label="Barrel" />
      </ReferenceInput>
      <CustomDateTimeInput source="datetime" label="Measurement Time" />
      <ReferenceInput source="deviceID" reference="devices">
        <SelectInput optionText="name" label="Device" />
      </ReferenceInput>
      <NumberInput source="fso2" label={"Free SO\u2082 (ppm)"} />
      <BooleanInput source="valid" label="Valid" defaultValue={"true"} />
      <TextInput source="notes" label="Notes" multiline={true} />
    </SimpleForm>
  </Edit>
);

// Available to admin only
export const FSO2Create = () => (
  <Create redirect="list">
    <SimpleForm>
      <NumberInput source="id" readOnly />
      <TextInput source="ticket" readOnly />
      <ReferenceInput source="barrelID" reference="vessels">
        <SelectInput optionText="barcode" label="Barrel" />
      </ReferenceInput>
      <CustomDateTimeInput source="datetime" label="Measurement Time" />
      <ReferenceInput source="deviceID" reference="devices">
        <SelectInput optionText="name" label="Device" />
      </ReferenceInput>
      <NumberInput source="fso2" label={"Free SO\u2082 (ppm)"} />
    </SimpleForm>
  </Create>
);

export const FSO2Show = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Overview">
        <AliasOrBarcodeReference
          label="Vessel"
          source="barrelID"
          reference="vessels"
          sortable={false}
        />
        <ReferenceField
          source="lotID"
          reference="lots"
          label="Lot"
          sortable={false}
          link="show"
          empty={"\u00A0"}
        >
          <LotNameField />
        </ReferenceField>
        <ReferenceField
          source="blendID"
          reference="blends"
          label="Blend"
          sortable={false}
          link="show"
          empty={"\u00A0"}
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField
          source="intendedUse"
          label="Intended Use"
          sortable={false}
          emptyText={"\u00A0"}
        />
        <NumberField
          source="fso2"
          label={"Free SO\u2082 (ppm)"}
          sortable={false}
          options={{ maximumFractionDigits: 0 }}
        />
        <CustomDatetimeField source="datetime" label="Measurement Time" />
        {hasScope(SCOPE.DIAGNOSTICS) ? (
          <TicketField source="ticket" label="Ticket" sortable={false} />
        ) : (
          <TextField source="ticket" label="Ticket" sortable={false} />
        )}

        {hasScope(SCOPE.DIAGNOSTICS) ? (
          <BooleanField source="valid" label="Valid" sortable={false} />
        ) : null}
        <DeviceTitleField label="Device" />
        {hasScope(SCOPE.DIAGNOSTICS) ? (
          <WineColourField source="isRed" label="Color" />
        ) : null}
        {hasScope(SCOPE.DIAGNOSTICS) ? (
          <NumberField source="calibration" label="Calibration" />
        ) : null}
        <ContextTagField source="context" />
        <TextField
          source="notes"
          label="Notes"
          sortable={false}
          emptyText={"\u00A0"}
        />
      </Tab>
      <CustomTab />
    </TabbedShowLayout>
  </Show>
);
